'use client';
import * as React from 'react';

/** Bullseye = origin marker (0.25σ + 1σ rings + crosshair). */
export function Bullseye({ cx, cy, innerRx, innerRy, outerRx, outerRy }: {
  cx: number; cy: number;
  innerRx: number; innerRy: number;
  outerRx: number; outerRy: number;
}) {
  return (
    <g>
      <ellipse cx={cx} cy={cy} rx={innerRx} ry={innerRy}
        fill="none" stroke="#000000" strokeOpacity={0.8} strokeWidth={2} strokeDasharray="4 4" />
      <ellipse cx={cx} cy={cy} rx={outerRx} ry={outerRy}
        fill="none" stroke="#000000" strokeOpacity={0.8} strokeWidth={2} strokeDasharray="4 4" />
      <line x1={cx - 8} y1={cy} x2={cx + 8} y2={cy}
        stroke="#000000" strokeOpacity={0.8} strokeWidth={2} strokeDasharray="4 4" />
      <line x1={cx} y1={cy - 8} x2={cx} y2={cy + 8}
        stroke="#000000" strokeOpacity={0.8} strokeWidth={2} strokeDasharray="4 4" />
      <text x={cx} y={cy - innerRy - 3}
        fill="#000000" fillOpacity={0.7} fontSize={11} fontWeight={600}
        textAnchor="middle" fontFamily="var(--font-mono), ui-monospace, monospace">
        0.25σ
      </text>
      <text x={cx} y={cy - outerRy - 3}
        fill="#000000" fillOpacity={0.7} fontSize={11} fontWeight={600}
        textAnchor="middle" fontFamily="var(--font-mono), ui-monospace, monospace">
        1σ
      </text>
    </g>
  );
}

/** X + Y axis tick labels and axis titles. */
export function AxisLabels({
  ticks, toX, toY, offsetX, offsetY, plotSizeX, plotSizeY, padding, yAxis,
}: {
  ticks: number[];
  toX: (t: number) => number;
  toY: (t: number) => number;
  offsetX: number;
  offsetY: number;
  plotSizeX: number;
  plotSizeY: number;
  padding: { top: number; left: number };
  yAxis: 'momentum' | 'valuation' | string;
}) {
  return (
    <>
      {ticks.map(t => (
        <text key={`xl${t}`} x={toX(t)} y={padding.top + offsetY + plotSizeY + 24}
          fill="#64748b" fontSize={10} textAnchor="middle"
          fontFamily="var(--font-mono), ui-monospace, monospace">
          {t}
        </text>
      ))}
      {ticks.map(t => (
        <text key={`yl${t}`} x={padding.left + offsetX - 12} y={toY(t) + 4}
          fill="#64748b" fontSize={10} textAnchor="end"
          fontFamily="var(--font-mono), ui-monospace, monospace">
          {yAxis === 'momentum' ? -t : t}
        </text>
      ))}
      <text x={padding.left + offsetX + plotSizeX / 2} y={padding.top + offsetY + plotSizeY + 44}
        fill="#64748b" fontSize={11} textAnchor="middle" fontWeight={500}
        fontFamily="var(--font-inter), system-ui, sans-serif" letterSpacing="0.025em">
        QUALITY SCORE
      </text>
      <text x={padding.left + offsetX - 44} y={padding.top + offsetY + plotSizeY / 2}
        fill="#64748b" fontSize={11} textAnchor="middle" fontWeight={500}
        fontFamily="var(--font-inter), system-ui, sans-serif" letterSpacing="0.025em"
        transform={`rotate(-90, ${padding.left + offsetX - 44}, ${padding.top + offsetY + plotSizeY / 2})`}>
        {yAxis === 'momentum' ? 'MOMENTUM SCORE' : 'VALUATION SCORE'}
      </text>
    </>
  );
}

/** Grid lines clipped to the plot area. */
export function GridLines({
  ticks, toX, toY, offsetX, offsetY, plotSizeX, plotSizeY, padding,
}: {
  ticks: number[];
  toX: (t: number) => number;
  toY: (t: number) => number;
  offsetX: number;
  offsetY: number;
  plotSizeX: number;
  plotSizeY: number;
  padding: { top: number; left: number };
}) {
  return (
    <g clipPath="url(#plotClip)">
      {ticks.map(t => (
        <line key={`xg${t}`} x1={toX(t)} y1={padding.top + offsetY} x2={toX(t)} y2={padding.top + offsetY + plotSizeY}
          stroke={t === 0 ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.08)'}
          strokeWidth={t === 0 ? 1.5 : 0.5} />
      ))}
      {ticks.map(t => (
        <line key={`yg${t}`} x1={padding.left + offsetX} y1={toY(t)} x2={padding.left + offsetX + plotSizeX} y2={toY(t)}
          stroke={t === 0 ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.08)'}
          strokeWidth={t === 0 ? 1.5 : 0.5} />
      ))}
    </g>
  );
}

/** Corner-quadrant text labels (e.g., "high quality + cheap"). */
export function CornerQuadrantLabels({
  labels,
}: {
  labels: Array<{ x: number; y: number; text: string; color: string; anchor?: 'start' | 'middle' | 'end' }>;
}) {
  return (
    <>
      {labels.map((l, i) => (
        <text key={i} x={l.x} y={l.y} fill={l.color} fontSize={9} opacity={0.45}
          textAnchor={l.anchor ?? 'start'}
          fontFamily="var(--font-inter), system-ui, sans-serif" fontWeight={500}>
          {l.text}
        </text>
      ))}
    </>
  );
}
